// Slice 11 — BesideU Admin sign-in entry (DESIGN §4.4 / §6.6). Submits an
// email to `/auth/admin/request`; the response is intentionally identical for
// known/unknown emails (enumeration defence — DESIGN §4.6). The follow-up
// flow lives at `/admin/totp` after the operator clicks the email link.

import { useState, type FormEvent } from 'react';
import { Link, redirect } from 'react-router';
import { Logo } from '@/components/brand/Logo.tsx';
import { api } from '@/lib/api.ts';
import { fetchMe, landingPathFor } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput } from '@/components/ui/Field.tsx';

export async function adminSignInLoader() {
  const session = await fetchMe();
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (session) throw redirect(landingPathFor(session.role));
  return null;
}

export function AdminSignIn() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.post('/auth/admin/request', { email });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">BesideU Admin sign-in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Operator-only. Not sure where you belong?{' '}
        <Link to="/login" className="underline underline-offset-2 hover:no-underline">
          See all sign-in options
        </Link>
        .
      </p>

      {submitted ? (
        <div className="mt-8 rounded-lg border border-border bg-muted/30 p-6 text-sm">
          <p className="font-medium">If your address is registered, a link is on its way.</p>
          <p className="mt-2 text-muted-foreground">
            The link expires in 15 minutes. After clicking, you&rsquo;ll be asked for a code from
            your authenticator app.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 grid gap-5">
          <Field label="Email">
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" loading={pending}>
            Send sign-in link
          </Button>
          {error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </main>
  );
}
